import React, { useEffect, useState } from 'react'
import { Mesh, MeshStandardMaterial, UnsignedByteType } from 'three'
import { useThree } from '@react-three/fiber'
import { PMREMGenerator } from 'three/src/extras/PMREMGenerator'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader'
import hdr from 'assets/gltfs/studio.hdr'
import { Html, useGLTF } from '@react-three/drei'
import { useMoveStore } from 'store/moveStore'
import { useScoreStore } from 'store/ScoreStore'
import { SceneHazardItem } from 'types/Hazard'

export type HazardProps = {
  hazard: SceneHazardItem
  enabled?: boolean
}

const Hazard = React.forwardRef<any, HazardProps>(({ enabled = true, ...props }: HazardProps, ref) => {
  // Load GLTF - Start
  const { scene } = useGLTF(props.hazard.path)
  const { gl } = useThree()
  const pmRemGenerator = new PMREMGenerator(gl)
  const loader = new RGBELoader()
  loader.setDataType(UnsignedByteType)
  pmRemGenerator.compileEquirectangularShader()

  useEffect(() => {
    loader.load(hdr, (texture) => {
      (scene as unknown as THREE.Scene).environment = pmRemGenerator.fromEquirectangular(texture).texture
      texture.dispose()
      pmRemGenerator.dispose()
    })
  }, [scene, loader, pmRemGenerator])
  // Load GLTF - Complete

  const [mouseDown, setMouseDown] = useState(false)
  const [selected, setSelected] = useState(false)
  const [answered, setAnswered] = useState(false)
  const handleObjectClickEvent = () => {
    if (selected || answered || useMoveStore.getState().movementIsLocked) return
    if (!enabled) return
    if (!mouseDown) return
    setMouseDown(false)

    scene.traverse((obj) => {
      if (obj.type === 'Mesh') {
        const material = (obj as Mesh).material as MeshStandardMaterial
        material.emissive.set('#FFFFFF')
        material.emissiveIntensity = 5

        setSelected(true)
        setTimeout(() => {
          // Workaround for three bug
          document.querySelector('#mainCanvas > div')?.removeAttribute('style')
        }, 100)
        useMoveStore.getState().lockMovement()
      }
    })
  }

  const handleYesClick = () => {
    if (props.hazard.value && props.hazard.value !== 0) {
      useScoreStore.getState().findHazard(props.hazard.id)
      useScoreStore.getState().increaseScore(props.hazard.value)
      useScoreStore.setState({ showDoneButton: true })

      correctAnswer()
    } else wrongAnswer()
  }

  const handleNoClick = () => {
    if (props.hazard.value && props.hazard.value !== 0) wrongAnswer()
    else correctAnswer()
  }

  const correctAnswer = () => {
    scene.traverse((obj) => {
      if (obj.type === 'Mesh') {
        const material = (obj as Mesh).material as MeshStandardMaterial
        material.emissive.set('#00FF00')
        material.emissiveIntensity = 1

        setSelected(false)
        setAnswered(true)
        useMoveStore.getState().unlockMovement()
      }
    })
  }

  const wrongAnswer = () => {
    scene.traverse((obj) => {
      if (obj.type === 'Mesh') {
        const material = (obj as Mesh).material as MeshStandardMaterial
        material.emissive.set('#ff0000')
        material.emissiveIntensity = 1

        setSelected(false)
        setAnswered(true)
        useMoveStore.getState().unlockMovement()
      }
    })
  }

  return (
    <group ref={ref}>
      <primitive {...props}
                 object={scene}
                 onPointerDown={() => setMouseDown(true)}
                 onPointerOut={() => setMouseDown(false)}
                 onPointerUp={handleObjectClickEvent} />
      {selected &&
        <Html prepend
              wrapperClass={'fixed inset-0 w-full h-full flex items-center justify-center bg-black bg-opacity-10'}
              calculatePosition={() => {
                return []
              }}>
          <div
            className={'flex flex-col items-center justify-center bg-white py-3 px-6 rounded-lg md:mt-[200px] animate splat'}>
            <h2>{props.hazard.title}</h2>
            <h3>Is this a hazard?</h3>
            <p className={'my-1 text-sm'}>{props.hazard.copy}</p>
            <div className={'flex space-x-4'}>
              <button className={'btn--blue'} onClick={handleYesClick}>
                Yes
              </button>

              <button className={'btn--blue'} onClick={handleNoClick}>
                No
              </button>
            </div>
          </div>
        </Html>
      }
    </group>
  )
})

Hazard.displayName = 'Hazard'

export default Hazard
